import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Clock, Copy, Share2, Settings, HelpCircle, LogOut, Pencil, Mail, ArrowLeft, Sparkles, CreditCard, ExternalLink } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RenameProjectModal } from "@/components/dashboard/RenameProjectModal";
import { VersionHistoryModal, Version } from "@/components/project/VersionHistoryModal";
import { CopyEmailModal } from "@/components/project/CopyEmailModal";
import { ShareModal } from "@/components/project/ShareModal";
import { Button } from "@/components/ui/button";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { GreenLightLogo } from "@/components/GreenLightLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useRenameProject, useDeleteVersion, useSetAwaitingNextVersion } from "@/hooks/useProjectMutations";
import HtmlEmailPreview, { HtmlEmailPreviewRef, CommentDotData } from "@/components/project/HtmlEmailPreview";
import { Comment } from "@/components/demo/CommentsPanel";
import CommentPopover from "@/components/demo/CommentPopover";
import CommentViewPopover from "@/components/project/CommentViewPopover";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { buildProjectEmail, getEmailDomain } from "@/lib/email-config";
import { Badge } from "@/components/ui/badge";
import { pushEmailVersionGenerated } from "@/lib/analytics";
import { logCommentingError } from "@/lib/errorLogger";
import { CommentsSidebar } from "@/components/project/CommentsSidebar";
import { Switch } from "@/components/ui/switch";
import { formatDistanceToNow } from "date-fns";
import { extractPreheader } from "@/lib/emailUtils";
import { useVariations, Variation } from "@/hooks/useVariations";
import { VariationDropdown } from "@/components/project/VariationDropdown";
import { RenameVariationModal } from "@/components/project/RenameVariationModal";
import { CreateVariationModal } from "@/components/project/CreateVariationModal";
import { ConfirmationDialog } from "@/components/dashboard/ConfirmationDialog";
import { UpgradeVersionsDialog } from "@/components/project/UpgradeVersionsDialog";
import { Lock } from "lucide-react";

// Helper functions
const getInitials = (name: string): string => {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const formatTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  return formatDistanceToNow(date, { addSuffix: true });
};

const ProjectView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user, signOut } = useAuth();
  const { subscribed, openCustomerPortal, tier } = useSubscription();
  const isFreeTier = tier === 'free';
  const [isUpgradeVersionsOpen, setIsUpgradeVersionsOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth", { replace: true });
  };

  const handleContactUsClick = () => {
    if (location.pathname === '/help') {
      const element = document.getElementById('contact-us');
      element?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/help');
      setTimeout(() => {
        const element = document.getElementById('contact-us');
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const getUserInitials = () => {
    const firstName = user?.user_metadata?.first_name || "";
    const lastName = user?.user_metadata?.last_name || "";
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return "U";
  };
  const queryClient = useQueryClient();
  const renameProject = useRenameProject();
  const deleteVersion = useDeleteVersion();
  const setAwaitingNextVersion = useSetAwaitingNextVersion();
  const previewRef = useRef<HTMLDivElement>(null);
  const emailPreviewRef = useRef<HtmlEmailPreviewRef>(null);
  
  // Variation state
  const [selectedVariationId, setSelectedVariationId] = useState<string | null>(null);
  const [variationToRename, setVariationToRename] = useState<Variation | null>(null);
  const [variationToDelete, setVariationToDelete] = useState<Variation | null>(null);
  const [isCreateVariationOpen, setIsCreateVariationOpen] = useState(false);
  
  const { data: project, isLoading } = useQuery({
    queryKey: ["project", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: versions } = useQuery({
    queryKey: ["versions", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("versions")
        .select("*")
        .eq("project_id", id!)
        .order("version_number", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.length) return 5000;
      if (project?.awaiting_next_version) return 5000;
      return false;
    },
  });

  const hasVersions = versions && versions.length > 0;
  const isAwaitingNextVersion = project?.awaiting_next_version === true;
  const previousVersionId = versions?.[0]?.id ?? null;

  // Track selected version - defaults to latest
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  // Initialize selectedVersionId when versions load
  useEffect(() => {
    if (versions && versions.length > 0 && !selectedVersionId) {
      setSelectedVersionId(versions[0].id);
    }
  }, [versions, selectedVersionId]);

  // Auto-switch to the new version when it arrives (webhook clears the awaiting flag
  // and inserts the new row). If we're awaiting and a fresh version appears, jump to it.
  const prevTopVersionIdRef = useRef<string | null>(null);
  useEffect(() => {
    const topId = versions?.[0]?.id ?? null;
    if (prevTopVersionIdRef.current && topId && topId !== prevTopVersionIdRef.current) {
      // A new latest version row appeared — select it.
      setSelectedVersionId(topId);
    }
    prevTopVersionIdRef.current = topId;
  }, [versions]);

  // While awaiting, also poll the project so we notice when the webhook clears the flag.
  useEffect(() => {
    if (!isAwaitingNextVersion || !id) return;
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
    }, 5000);
    return () => clearInterval(interval);
  }, [isAwaitingNextVersion, id, queryClient]);

  // Track untracked versions for analytics
  useEffect(() => {
    if (!versions || versions.length === 0 || !id) return;
    
    // Find versions that haven't been tracked yet
    const untrackedVersions = versions.filter(v => !v.analytics_event_sent);
    
    if (untrackedVersions.length === 0) return;
    
    // Fire event for each untracked version
    untrackedVersions.forEach(version => {
      pushEmailVersionGenerated({
        project_id: version.project_id,
        version_number: version.version_number,
        source: "inbound_email",
      });
    });
    
    // Mark all as tracked in database
    const untrackedIds = untrackedVersions.map(v => v.id);
    supabase
      .from("versions")
      .update({ analytics_event_sent: true })
      .in("id", untrackedIds)
      .then(({ error }) => {
        if (error) {
          console.error("Failed to mark versions as tracked:", error);
        }
      });
  }, [versions, id]);

  // Get current version data
  const currentVersion = versions?.find(v => v.id === selectedVersionId) ?? versions?.[0];
  
  // Fetch variations for the current version
  const {
    variations,
    renameVariation,
    deleteVariation,
    createPendingVariation,
    cancelPendingVariation,
  } = useVariations(currentVersion?.id);

  // Get the selected variation content (if any)
  const selectedVariation = selectedVariationId
    ? variations.find(v => v.id === selectedVariationId)
    : null;

  const isAwaitingVariation = !!selectedVariation?.pending;

  // Use variation content if selected, otherwise use version's default content.
  // Pending variations have no content yet — fall back to empty so the awaiting
  // screen renders cleanly instead of showing the default email under the badge.
  const htmlContent = selectedVariation
    ? (selectedVariation.html_content ?? "")
    : (currentVersion?.html_content ?? "");
  const displaySubject = selectedVariation
    ? selectedVariation.subject
    : currentVersion?.subject;
  
  // Reset variation selection when version changes
  useEffect(() => {
    setSelectedVariationId(null);
  }, [currentVersion?.id]);
  
  // Extract preheader text from email HTML
  const preheaderText = useMemo(() => extractPreheader(htmlContent), [htmlContent]);
  
  // Check if viewing the latest version (non-latest versions are read-only)
  const isLatestVersion = hasVersions && selectedVersionId === versions[0].id;

  // Fetch comments for selected version, filtered by variation
  const { data: versionComments, refetch: refetchComments } = useQuery({
    queryKey: ["comments", selectedVersionId, selectedVariationId],
    queryFn: async () => {
      let query = supabase
        .from("comments")
        .select("*")
        .eq("version_id", selectedVersionId!);
      
      // Filter by variation: null = default, otherwise specific variation
      if (selectedVariationId) {
        query = query.eq("variation_id", selectedVariationId);
      } else {
        query = query.is("variation_id", null);
      }
      
      const { data, error } = await query.order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!selectedVersionId,
  });

  // Mobile preview toggle state
  const [isMobilePreview, setIsMobilePreview] = useState(false);
  const currentDeviceType = isMobilePreview ? 'mobile' : 'desktop';
  
  // Test links mode state
  const [isTestLinksEnabled, setIsTestLinksEnabled] = useState(false);
  const [isTestLinksTooltipOpen, setIsTestLinksTooltipOpen] = useState(false);
  const [isMobileTooltipOpen, setIsMobileTooltipOpen] = useState(false);

  // Transform DB comments to Comment interface
  const formattedComments: Comment[] = (versionComments || []).map(c => ({
    id: c.id,
    author: c.author_name || "Anonymous",
    initials: getInitials(c.author_name || "Anonymous"),
    content: c.content,
    timestamp: formatTimestamp(c.created_at),
    rawTimestamp: c.created_at,
    positionX: c.position_x ?? undefined,
    positionY: c.position_y ?? undefined,
    authorId: c.author_id || undefined,
    deviceType: (c.device_type as 'desktop' | 'mobile') || 'desktop',
  }));

  // Transform comments to dots for the email preview - filter by current device type
  const commentDots: CommentDotData[] = formattedComments
    .filter(c => 
      c.positionX !== undefined && 
      c.positionY !== undefined &&
      c.deviceType === currentDeviceType
    )
    .map(c => ({
      id: c.id,
      positionX: c.positionX!,
      positionY: c.positionY!,
    }));
  
  const projectName = project?.name ?? "Loading...";
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isCopyEmailModalOpen, setIsCopyEmailModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  
  // Sidebar state - collapsed by default on mobile/tablet (<1024px)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 1024);
  
  // Position-based commenting state
  const [pendingDotPosition, setPendingDotPosition] = useState<{ x: number; y: number } | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  
  // View comment popover state
  const [viewingComment, setViewingComment] = useState<Comment | null>(null);
  const [viewPopoverPosition, setViewPopoverPosition] = useState<{ top: number; left: number } | null>(null);

  const handleCopyOnboardingEmail = useCallback(() => {
    const emailAddress = project?.email_prefix ? buildProjectEmail(project.email_prefix) : "";
    navigator.clipboard.writeText(emailAddress);
    toast.success("Email address copied!");
  }, [project?.email_prefix]);

  const handlePositionClick = useCallback((positionX: number, positionY: number, screenRect: DOMRect) => {
    setPendingDotPosition({ x: positionX, y: positionY });
    
    const popoverWidth = 320;
    const popoverHeight = 200;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth < 768;
    
    // Calculate screen position from percentage
    const clickX = screenRect.left + (positionX / 100) * screenRect.width;
    const clickY = screenRect.top + (positionY / 100) * screenRect.height;
    
    let left: number;
    let top: number;
    
    if (isMobile) {
      left = Math.max(16, (viewportWidth - popoverWidth) / 2);
      top = clickY + 24;
      if (top + popoverHeight > viewportHeight - 16) {
        top = clickY - popoverHeight - 24;
      }
      if (top < 16) {
        top = 16;
      }
    } else {
      left = clickX + 24;
      top = clickY - 20;
      if (left + popoverWidth > viewportWidth - 16) {
        left = clickX - popoverWidth - 24;
      }
      if (left < 16) {
        left = Math.max(16, (viewportWidth - popoverWidth) / 2);
      }
      if (top + popoverHeight > viewportHeight - 16) {
        top = viewportHeight - popoverHeight - 16;
      }
      if (top < 16) {
        top = 16;
      }
    }
    
    setPopoverPosition({ top, left });
  }, []);

  const handleClosePopover = useCallback(() => {
    setPendingDotPosition(null);
    setPopoverPosition(null);
  }, []);

  const handleCloseViewPopover = useCallback(() => {
    setViewingComment(null);
    setViewPopoverPosition(null);
  }, []);

  const handlePostComment = useCallback(async (positionX: number, positionY: number, content: string) => {
    if (!selectedVersionId || !user) return;
    
    const authorName = user.user_metadata?.first_name 
      ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`.trim()
      : user.email || "You";
    
    const { error } = await supabase.from("comments").insert({
      version_id: selectedVersionId,
      content,
      position_x: positionX,
      position_y: positionY,
      author_id: user.id,
      author_name: authorName,
      device_type: currentDeviceType,
      variation_id: selectedVariationId,
    });

    if (error) {
      logCommentingError('Failed to post position comment', error, { 
        versionId: selectedVersionId, 
        positionX,
        positionY,
        variationId: selectedVariationId,
      });
      toast.error("Failed to post comment");
      return;
    }

    refetchComments();
    handleClosePopover();
  }, [selectedVersionId, selectedVariationId, user, currentDeviceType, refetchComments, handleClosePopover]);

  const handleAddGeneralComment = useCallback(async (content: string) => {
    if (!selectedVersionId || !user) return;
    
    const authorName = user.user_metadata?.first_name 
      ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`.trim()
      : user.email || "You";
    
    const { error } = await supabase.from("comments").insert({
      version_id: selectedVersionId,
      content,
      author_id: user.id,
      author_name: authorName,
      device_type: currentDeviceType,
      variation_id: selectedVariationId,
    });

    if (error) {
      logCommentingError('Failed to post general comment', error, { 
        versionId: selectedVersionId,
        variationId: selectedVariationId,
      });
      toast.error("Failed to post comment");
      return;
    }

    refetchComments();
  }, [selectedVersionId, selectedVariationId, user, currentDeviceType, refetchComments]);

  const handleDeleteComment = useCallback(async (commentId: string) => {
    const { error } = await supabase
      .from("comments")
      .delete()
      .eq("id", commentId);

    if (error) {
      logCommentingError('Failed to delete comment', error, { commentId });
      toast.error("Failed to delete comment");
      return;
    }

    toast.success("Comment deleted");
    refetchComments();
  }, [refetchComments]);

  const handleDotClick = useCallback((commentId: string, screenRect: DOMRect, positionX: number, positionY: number) => {
    const comment = formattedComments.find(c => c.id === commentId);
    if (!comment) return;
    
    // Calculate popover position
    const popoverWidth = 288; // w-72 = 18rem = 288px
    const popoverHeight = 120;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    const clickX = screenRect.left + (positionX / 100) * screenRect.width;
    const clickY = screenRect.top + (positionY / 100) * screenRect.height;
    
    let left = clickX + 24;
    let top = clickY - 20;
    
    // Adjust if overflowing right
    if (left + popoverWidth > viewportWidth - 16) {
      left = clickX - popoverWidth - 24;
    }
    if (left < 16) {
      left = Math.max(16, (viewportWidth - popoverWidth) / 2);
    }
    
    // Adjust if overflowing bottom
    if (top + popoverHeight > viewportHeight - 16) {
      top = viewportHeight - popoverHeight - 16;
    }
    if (top < 16) {
      top = 16;
    }
    
    setViewingComment(comment);
    setViewPopoverPosition({ top, left });
    setHighlightedCommentId(commentId);
    
    // Clear highlight after a delay
    setTimeout(() => {
      setHighlightedCommentId(null);
    }, 3000);
  }, [formattedComments]);

  const handleCommentClick = useCallback((commentId: string) => {
    const comment = formattedComments.find(c => c.id === commentId);
    if (comment?.positionX !== undefined && comment?.positionY !== undefined) {
      // Auto-toggle preview mode to match comment's device type
      const targetDeviceType = comment.deviceType || 'desktop';
      const needsToggle = (targetDeviceType === 'mobile') !== isMobilePreview;
      
      if (needsToggle) {
        setIsMobilePreview(targetDeviceType === 'mobile');
      }
      
      setHighlightedCommentId(commentId);
      
      // Small delay to allow preview mode change to render before scrolling
      setTimeout(() => {
        emailPreviewRef.current?.scrollToPosition(comment.positionX!, comment.positionY!);
      }, needsToggle ? 100 : 0);
      
      setTimeout(() => {
        setHighlightedCommentId(null);
      }, 3000);
    }
  }, [formattedComments, isMobilePreview]);

  const handleCommentHover = useCallback((commentId: string | null) => {
    // We can optionally highlight the dot on hover
    if (commentId) {
      setHighlightedCommentId(commentId);
    } else {
      setHighlightedCommentId(null);
    }
  }, []);

  const handleRenameProject = useCallback((projectId: string, newName: string) => {
    renameProject.mutate({ id: projectId, name: newName }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["project", id] });
      }
    });
  }, [renameProject, queryClient, id]);

  const handleSelectVersion = useCallback((version: Version) => {
    setSelectedVersionId(version.id);
    setIsVersionHistoryOpen(false);
  }, []);

  const handleDeleteVersion = useCallback(async (version: Version) => {
    if (!id) return;
    
    await deleteVersion.mutateAsync({
      versionId: version.id,
      projectId: id,
    });
    
    // If the deleted version was being viewed, switch to the most recent remaining version
    if (selectedVersionId === version.id) {
      const remainingVersions = versions?.filter(v => v.id !== version.id) || [];
      if (remainingVersions.length > 0) {
        setSelectedVersionId(remainingVersions[0].id);
      } else {
        setSelectedVersionId(null);
      }
    }
    
    toast.success("Version deleted");
  }, [id, selectedVersionId, versions, deleteVersion]);

  const handleCreateNewVersion = useCallback(async () => {
    if (!id) return;
    try {
      // Flip the project flag — no empty version row is created. The webhook will
      // insert a new version row when the next email arrives, then clear the flag.
      await setAwaitingNextVersion.mutateAsync({ projectId: id, awaiting: true });
      toast.success("Send your next email — we'll create a new version when it arrives.");
    } catch (error) {
      // Error handling is in the mutation hook
    }
  }, [id, setAwaitingNextVersion]);

  const handleCancelNewVersion = useCallback(async () => {
    if (!id) return;
    try {
      await setAwaitingNextVersion.mutateAsync({ projectId: id, awaiting: false });
      toast.success("Returned to previous version");
    } catch (error) {
      // Error handling is in the mutation hook
    }
  }, [id, setAwaitingNextVersion]);

  // Content for when project has versions (with sidebar)
  const renderWithSidebar = () => (
    <>
      <div className="h-screen bg-background grain flex flex-col overflow-hidden" onClick={handleClosePopover}>
        {/* Header */}
        <header className="sticky top-0 z-50 bg-white border-b border-foreground/5">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => navigate('/dashboard')} 
                className="flex items-center gap-2"
              >
                <GreenLightLogo width={190} animate={false} />
              </button>

              <div className="flex items-center gap-6">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors">
                      <HelpCircle className="h-4 w-4" />
                      Help
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/help')}>
                      How to Use
                    </DropdownMenuItem>
                    <DropdownMenuItem className="cursor-pointer" onClick={handleContactUsClick}>
                      Contact Us
                    </DropdownMenuItem>
                    <DropdownMenuItem className="cursor-pointer" onClick={() => window.open('https://form.typeform.com/to/DU5hRCud', '_blank')}>
                      Provide Feedback
                      <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="rounded-full focus:outline-none ring-2 ring-accent ring-offset-2">
                      <Avatar className="h-9 w-9 cursor-pointer">
                        <AvatarFallback className="bg-foreground text-background font-medium">{getUserInitials()}</AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/plans')}>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Plans
                      {!subscribed && <Badge variant="secondary" className="ml-auto text-xs">Upgrade</Badge>}
                    </DropdownMenuItem>
                    {subscribed && (
                      <DropdownMenuItem className="cursor-pointer" onClick={openCustomerPortal}>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Billing
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings')}>
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      className="cursor-pointer text-destructive focus:text-destructive"
                      onClick={handleSignOut}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </header>

        {/* Project Info Bar */}
        <div className="border-b border-foreground/5 bg-white">
          <div className="container mx-auto px-6 py-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <button
                  onClick={() => {
                    if (isAwaitingVariation) {
                      setSelectedVariationId(null);
                    } else {
                      navigate('/dashboard');
                    }
                  }}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground flex-shrink-0"
                  title={isAwaitingVariation ? "Back to project" : "Back to Dashboard"}
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                {isAwaitingVariation ? (
                  <span className="font-display font-bold text-foreground text-xl md:text-2xl flex items-center gap-2 flex-1 min-w-0">
                    <span className="truncate">{projectName}</span>
                  </span>
                ) : (
                  <button 
                    onClick={() => setIsRenameModalOpen(true)}
                    className="font-display font-bold text-foreground text-xl md:text-2xl hover:underline transition-colors cursor-pointer group flex items-center gap-2 flex-1 min-w-0"
                  >
                    <span className="truncate">{projectName}</span>
                    <Pencil className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground flex-shrink-0" />
                  </button>
                )}
                {!isAwaitingVariation && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors ml-[10px]">
                        <Clock className="w-4 h-4" />
                        <span className="underline">Versions</span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-64">
                      <DropdownMenuItem
                        className="flex items-start py-3 cursor-pointer gap-2"
                        onClick={isFreeTier ? () => setIsUpgradeVersionsOpen(true) : handleCreateNewVersion}
                      >
                        <div className="flex flex-col flex-1">
                          <span className="font-medium flex items-center gap-2">
                            Create a new version
                            {isFreeTier && <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
                          </span>
                          <span className="text-xs text-muted-foreground mt-0.5">
                            {isFreeTier ? 'Available on paid plans' : 'Start fresh with a new iteration'}
                          </span>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="flex flex-col items-start py-3 cursor-pointer"
                        onClick={() => setIsVersionHistoryOpen(true)}
                      >
                        <span className="font-medium">Manage versions</span>
                        <span className="text-xs text-muted-foreground mt-0.5">
                          Browse and manage your versions here
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {!isLatestVersion && (
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground rounded-full">
                    Read-only
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {!isAwaitingVariation && (
                  <>
                    <Button 
                      variant="outline" 
                      className="flex items-center gap-2"
                      onClick={() => setIsCopyEmailModalOpen(true)}
                    >
                      <Copy className="w-4 h-4" />
                      Copy Email Address
                    </Button>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="default" 
                            className="bg-foreground text-primary-foreground hover:bg-acid-green hover:text-foreground"
                            onClick={() => setIsShareModalOpen(true)}
                          >
                            <Share2 className="w-4 h-4 mr-2" />
                            Share
                          </Button>
                        </TooltipTrigger>
                      </Tooltip>
                    </TooltipProvider>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area with Sidebar */}
        <div className="flex flex-1 w-full min-h-0 min-w-0 relative">
          <main className="flex-1 flex flex-col min-h-0 min-w-0">
            {/* Scrollable Email Preview Area */}
            <div 
              className="flex-1 overflow-auto pb-20 md:pb-8 touch-pan-x min-w-0"
              style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
            >
              {/* Subject Line Section - scrolls on mobile, sticky on tablet/desktop */}
              {displaySubject && (
                <div className="bg-foreground text-primary-foreground px-6 py-4 sticky left-0 md:top-0 md:z-20">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                    <div className="w-full md:flex-1 min-w-0 md:mr-6 flex flex-col gap-1">
                      <p className="text-xs text-primary-foreground/60 uppercase tracking-wide">Subject</p>
                      <p className="font-medium text-primary-foreground">{displaySubject}</p>
                    </div>
                    <div className="flex items-start justify-between md:justify-end gap-6 flex-shrink-0 mt-4 md:mt-0">
                      {/* Variation Dropdown */}
                      <VariationDropdown
                        variations={variations}
                        selectedVariationId={selectedVariationId}
                        onSelect={setSelectedVariationId}
                        onCreate={() => setIsCreateVariationOpen(true)}
                        onCancelPending={(v) => {
                          if (selectedVariationId === v.id) setSelectedVariationId(null);
                          cancelPendingVariation.mutate(v.id);
                        }}
                        onRename={setVariationToRename}
                        onDelete={setVariationToDelete}
                        isReadOnly={!isLatestVersion}
                      />
                      
                      {/* View Mobile Toggle */}
                      <div className="flex flex-col items-start md:items-end gap-1">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-primary-foreground/60 uppercase tracking-wide">View Mobile</span>
                          <TooltipProvider>
                            <Tooltip open={isMobileTooltipOpen} onOpenChange={setIsMobileTooltipOpen}>
                              <TooltipTrigger asChild>
                                <button 
                                  type="button"
                                  onClick={() => setIsMobileTooltipOpen(!isMobileTooltipOpen)}
                                  className="focus:outline-none"
                                >
                                  <HelpCircle className="w-3.5 h-3.5 text-primary-foreground/40 hover:text-primary-foreground/60 cursor-help" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs">
                                <p>Preview how this email may appear on a mobile device. This is an approximation — actual rendering may vary across different mobile email clients.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Switch
                          checked={isMobilePreview}
                          onCheckedChange={setIsMobilePreview}
                          className="data-[state=checked]:bg-teal-500 data-[state=unchecked]:bg-gray-600"
                        />
                      </div>
                      
                      {/* Test Links Toggle */}
                      <div className="flex flex-col items-start md:items-end gap-1">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-primary-foreground/60 uppercase tracking-wide">Test Links</span>
                          <TooltipProvider>
                            <Tooltip open={isTestLinksTooltipOpen} onOpenChange={setIsTestLinksTooltipOpen}>
                              <TooltipTrigger asChild>
                                <button 
                                  type="button"
                                  onClick={() => setIsTestLinksTooltipOpen(!isTestLinksTooltipOpen)}
                                  className="focus:outline-none"
                                >
                                  <HelpCircle className="w-3.5 h-3.5 text-primary-foreground/40 hover:text-primary-foreground/60 cursor-help" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs">
                                <p>Enable to hover over and click links in the email preview. Comment annotations will be hidden while testing links.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Switch
                          checked={isTestLinksEnabled}
                          onCheckedChange={setIsTestLinksEnabled}
                          className="data-[state=checked]:bg-teal-500 data-[state=unchecked]:bg-gray-600"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Preheader Row - scrolls with email content */}
              {preheaderText && (
                <div className="bg-muted px-6 py-2 sticky left-0 z-10">
                  <p className="text-xs">
                    <span className="text-muted-foreground uppercase tracking-wide mr-2">Preheader</span>
                    <span className="text-foreground">{preheaderText}</span>
                  </p>
                </div>
              )}
              
              {isAwaitingVariation && selectedVariation ? (
                <div className="md:container md:mx-auto px-4 md:px-6 py-16">
                  <div className="flex flex-col items-center justify-center text-center max-w-md mx-auto">
                    <div className="relative w-20 h-20 flex items-center justify-center mb-6">
                      <div className="absolute inset-0 rounded-full bg-accent/60 animate-pulse-ring" />
                      <div className="absolute inset-0 rounded-full bg-accent/40 animate-pulse-ring animation-delay-500" />
                      <div className="relative w-16 h-16 bg-foreground rounded-full flex items-center justify-center">
                        <Mail className="w-8 h-8 text-background" />
                      </div>
                    </div>
                    <h2 className="font-display font-bold text-2xl text-foreground mb-2">
                      Awaiting variation: {selectedVariation.name}
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Send your variation email from your ESP to the address below. It will appear here automatically.
                    </p>
                    <div className="bg-muted/50 rounded-lg p-4 w-full mb-4">
                      <p className="text-sm text-muted-foreground mb-2">Send your email to:</p>
                      <div className="flex items-center gap-2">
                        <code className="bg-background border border-border rounded px-3 py-2 text-foreground font-mono text-sm flex-1 break-all">
                          {project?.email_prefix
                            ? `${project.email_prefix}+${selectedVariation.tag}@${getEmailDomain()}`
                            : "Loading..."}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (!project?.email_prefix) return;
                            navigator.clipboard.writeText(
                              `${project.email_prefix}+${selectedVariation.tag}@${getEmailDomain()}`
                            );
                            toast.success("Copied to clipboard");
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        It usually takes about 30 seconds for your email to appear after sending.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedVariationId(null);
                        cancelPendingVariation.mutate(selectedVariation.id);
                      }}
                      className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4"
                    >
                      Cancel this variation
                    </button>
                  </div>
                </div>
              ) : (
                <div className="md:container md:mx-auto px-4 md:px-6 py-8">
                  <div
                    ref={previewRef}
                    className="mx-auto pr-4 md:pr-0"
                    style={{ width: 'max-content' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <HtmlEmailPreview
                      ref={emailPreviewRef}
                      htmlContent={htmlContent}
                      commentDots={commentDots}
                      highlightedCommentId={highlightedCommentId}
                      pendingDotPosition={pendingDotPosition}
                      onPositionClick={handlePositionClick}
                      onDotClick={handleDotClick}
                      isReadOnly={!isLatestVersion}
                      isMobilePreview={isMobilePreview}
                      isTestLinksEnabled={isTestLinksEnabled}
                    />
                  </div>
                </div>
              )}
            </div>
          </main>
          
          {!isAwaitingVariation && (
            <CommentsSidebar
              comments={formattedComments}
              highlightedCommentId={highlightedCommentId}
              onCommentClick={handleCommentClick}
              onAddComment={handleAddGeneralComment}
              onDeleteComment={handleDeleteComment}
              currentUserId={user?.id}
              isReadOnly={!isLatestVersion}
              isOpen={isSidebarOpen}
              onOpenChange={setIsSidebarOpen}
              variationTag={variations.length > 0 ? (selectedVariation?.name || null) : undefined}
            />
          )}
        </div>

        {/* Comment Popover */}
        {pendingDotPosition && popoverPosition && (
          <div onClick={(e) => e.stopPropagation()}>
            <CommentPopover
              position={popoverPosition}
              dotPosition={pendingDotPosition}
              onPost={handlePostComment}
              onClose={handleClosePopover}
            />
          </div>
        )}

        {/* Comment View Popover */}
        {viewingComment && viewPopoverPosition && (
          <div onClick={(e) => e.stopPropagation()}>
            <CommentViewPopover
              comment={viewingComment}
              position={viewPopoverPosition}
              onClose={handleCloseViewPopover}
            />
          </div>
        )}

        {/* Modals */}
        <RenameProjectModal
          open={isRenameModalOpen}
          onOpenChange={setIsRenameModalOpen}
          projectId={id!}
          currentName={projectName}
          onRename={handleRenameProject}
        />
        <UpgradeVersionsDialog
          open={isUpgradeVersionsOpen}
          onOpenChange={setIsUpgradeVersionsOpen}
        />
        <VersionHistoryModal
          open={isVersionHistoryOpen}
          onOpenChange={setIsVersionHistoryOpen}
          onSelectVersion={handleSelectVersion}
          onDeleteVersion={handleDeleteVersion}
          currentVersionId={selectedVersionId}
          versions={(versions || []).map((v, index) => ({
            id: v.id,
            name: `Version ${v.version_number}`,
            createdAt: new Date(v.created_at),
            isLatest: index === 0,
          }))}
        />
        <CopyEmailModal
          open={isCopyEmailModalOpen}
          onOpenChange={setIsCopyEmailModalOpen}
          emailPrefix={project?.email_prefix || ''}
        />
        <ShareModal
          open={isShareModalOpen}
          onOpenChange={setIsShareModalOpen}
          projectId={id || ''}
        />
                  <RenameVariationModal
          variation={variationToRename}
          existingNames={variations.map(v => v.name)}
          isOpen={!!variationToRename}
          onClose={() => setVariationToRename(null)}
          onRename={(variationId, newName) => {
            renameVariation.mutate({ variationId, newName }, {
              onSuccess: () => setVariationToRename(null),
            });
          }}
          isPending={renameVariation.isPending}
        />
        <CreateVariationModal
          isOpen={isCreateVariationOpen}
          onClose={() => setIsCreateVariationOpen(false)}
          emailPrefix={project?.email_prefix || ""}
          existingNames={variations.map((v) => v.name)}
          existingTags={variations.map((v) => v.tag)}
          isPending={createPendingVariation.isPending}
          onCreate={(name) => {
            createPendingVariation.mutate(
              { name },
              {
                onSuccess: (newVariation) => {
                  setIsCreateVariationOpen(false);
                  setSelectedVariationId(newVariation.id);
                  navigator.clipboard
                    .writeText(
                      `${project?.email_prefix}+${newVariation.tag}@${getEmailDomain()}`
                    )
                    .catch(() => {});
                  toast.success(`Variation "${newVariation.name}" created — address copied`);
                },
              }
            );
          }}
        />
        <ConfirmationDialog
          open={!!variationToDelete}
          onOpenChange={(open) => !open && setVariationToDelete(null)}
          title="Delete Variation"
          description={`Are you sure you want to delete the "${variationToDelete?.name}" variation? All comments on this variation will also be deleted.`}
          confirmLabel="Delete"
          variant="destructive"
          onConfirm={() => {
            if (variationToDelete) {
              // If deleting the currently selected variation, switch to default
              if (selectedVariationId === variationToDelete.id) {
                setSelectedVariationId(null);
              }
              deleteVariation.mutate(variationToDelete.id);
              setVariationToDelete(null);
            }
          }}
        />
      </div>
    </>
  );

  // Content for when project has no versions (onboarding)
  const renderWithoutSidebar = () => (
    <div className="min-h-screen bg-background grain" onClick={handleClosePopover}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-foreground/5">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="flex items-center gap-2"
            >
              <GreenLightLogo width={190} animate={false} />
            </button>

            <div className="flex items-center gap-6">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors">
                    <HelpCircle className="h-4 w-4" />
                    Help
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/help')}>
                    How to Use
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={handleContactUsClick}>
                    Contact Us
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => window.open('https://form.typeform.com/to/DU5hRCud', '_blank')}>
                    Provide Feedback
                    <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full focus:outline-none ring-2 ring-accent ring-offset-2">
                    <Avatar className="h-9 w-9 cursor-pointer">
                      <AvatarFallback className="bg-foreground text-background font-medium">{getUserInitials()}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/plans')}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Plans
                    {!subscribed && <Badge variant="secondary" className="ml-auto text-xs">Upgrade</Badge>}
                  </DropdownMenuItem>
                  {subscribed && (
                    <DropdownMenuItem className="cursor-pointer" onClick={openCustomerPortal}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Billing
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer text-destructive focus:text-destructive"
                    onClick={handleSignOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Project Info Bar */}
      <div className="border-b border-foreground/5 bg-white">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                title="Back to Dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setIsRenameModalOpen(true)}
                className="font-display font-bold text-foreground text-2xl hover:underline transition-colors cursor-pointer group flex items-center gap-2"
              >
                {projectName}
                <Pencil className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => setIsCopyEmailModalOpen(true)}
              >
                <Copy className="w-4 h-4" />
                Copy Email Address
              </Button>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block cursor-not-allowed">
                      <Button 
                        variant="default" 
                        className="bg-foreground text-primary-foreground hover:bg-acid-green hover:text-foreground pointer-events-none"
                        disabled
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Send a test email first before sharing for feedback</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Content */}
      <section className="py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center justify-center py-16 text-center max-w-md mx-auto">
            <div className="relative w-20 h-20 flex items-center justify-center mb-6">
              <div className="absolute inset-0 rounded-full bg-acid-green/60 animate-pulse-ring" />
              <div className="absolute inset-0 rounded-full bg-acid-green/40 animate-pulse-ring animation-delay-500" />
              <div className="relative w-16 h-16 bg-foreground rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-background" />
              </div>
            </div>
            <h2 className="font-display font-bold text-2xl text-foreground mb-4">
              Get Started with Your First Email
            </h2>
            <p className="text-muted-foreground mb-6">
              Send a test of your draft email to the address below to start collecting feedback.
            </p>
            <div className="bg-muted/50 rounded-lg p-4 w-full">
              <p className="text-sm text-muted-foreground mb-2">Send your email to:</p>
              <div className="flex items-center gap-2">
                <code className="bg-background border border-border rounded px-3 py-2 text-foreground font-mono text-sm flex-1">
                  {project?.email_prefix ? buildProjectEmail(project.email_prefix) : "Loading..."}
                </code>
                <Button variant="outline" size="sm" onClick={handleCopyOnboardingEmail}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                It usually takes about 30 seconds for your email to appear after sending.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Modals */}
      <RenameProjectModal
        open={isRenameModalOpen}
        onOpenChange={setIsRenameModalOpen}
        projectId={id!}
        currentName={projectName}
        onRename={handleRenameProject}
      />
      <CopyEmailModal
        open={isCopyEmailModalOpen}
        onOpenChange={setIsCopyEmailModalOpen}
        emailPrefix={project?.email_prefix || ''}
      />
    </div>
  );

  // Content for when user is creating a new version (bespoke screen with cancel option)
  const renderNewVersionScreen = () => (
    <div className="min-h-screen bg-background grain" onClick={handleClosePopover}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-foreground/5">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="flex items-center gap-2"
            >
              <GreenLightLogo width={190} animate={false} />
            </button>

            <div className="flex items-center gap-6">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors">
                    <HelpCircle className="h-4 w-4" />
                    Help
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/help')}>
                    How to Use
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={handleContactUsClick}>
                    Contact Us
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => window.open('https://form.typeform.com/to/DU5hRCud', '_blank')}>
                    Provide Feedback
                    <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full focus:outline-none ring-2 ring-accent ring-offset-2">
                    <Avatar className="h-9 w-9 cursor-pointer">
                      <AvatarFallback className="bg-foreground text-background font-medium">{getUserInitials()}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/plans')}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Plans
                    {!subscribed && <Badge variant="secondary" className="ml-auto text-xs">Upgrade</Badge>}
                  </DropdownMenuItem>
                  {subscribed && (
                    <DropdownMenuItem className="cursor-pointer" onClick={openCustomerPortal}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Billing
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer text-destructive focus:text-destructive"
                    onClick={handleSignOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Project Info Bar */}
      <div className="border-b border-foreground/5 bg-white">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                title="Back to Dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <span className="font-display font-bold text-foreground text-2xl flex items-center gap-2">
                {projectName}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* New Version Onboarding Content */}
      <section className="py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center justify-center py-16 text-center max-w-md mx-auto">
            {/* Animated mail icon */}
            <div className="relative w-20 h-20 flex items-center justify-center mb-6">
              <div className="absolute inset-0 rounded-full bg-accent/60 animate-pulse-ring" />
              <div className="absolute inset-0 rounded-full bg-accent/40 animate-pulse-ring animation-delay-500" />
              <div className="relative w-16 h-16 bg-foreground rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-background" />
              </div>
            </div>
            
            {/* Updated messaging for new version */}
            <h2 className="font-display font-bold text-2xl text-foreground mb-4">
              Send Your Next Version
            </h2>
            <p className="text-muted-foreground mb-6">
              Send an updated test email to the address below to start collecting feedback on this new iteration.
            </p>
            
            {/* Email address box */}
            <div className="bg-muted/50 rounded-lg p-4 w-full mb-6">
              <p className="text-sm text-muted-foreground mb-2">Send your email to:</p>
              <div className="flex items-center gap-2">
                <code className="bg-background border border-border rounded px-3 py-2 text-foreground font-mono text-sm flex-1">
                  {project?.email_prefix ? buildProjectEmail(project.email_prefix) : "Loading..."}
                </code>
                <Button variant="outline" size="sm" onClick={handleCopyOnboardingEmail}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                It usually takes about 30 seconds for your email to appear after sending.
              </p>
            </div>
            
            {/* Go back option */}
            <button
              onClick={handleCancelNewVersion}
              className="flex flex-col items-center gap-1 p-4 rounded-lg border border-foreground/20 bg-muted/30 hover:bg-accent hover:border-accent hover:text-accent-foreground transition-colors w-full group"
            >
              <div className="flex items-center gap-2 text-foreground group-hover:text-accent-foreground transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="font-medium">Go back to previous version</span>
              </div>
              <span className="text-xs text-muted-foreground group-hover:text-accent-foreground/70">
                Cancel this version and return to Version {versions?.find(v => v.id === previousVersionId)?.version_number || ""}
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Modals */}
      <RenameProjectModal
        open={isRenameModalOpen}
        onOpenChange={setIsRenameModalOpen}
        projectId={id!}
        currentName={projectName}
        onRename={handleRenameProject}
      />
      <CopyEmailModal
        open={isCopyEmailModalOpen}
        onOpenChange={setIsCopyEmailModalOpen}
        emailPrefix={project?.email_prefix || ''}
      />
    </div>
  );

  // Show new version screen when the project is awaiting its next email AND we
  // already have at least one version (i.e. there's a "previous version" to revert to).
  if (isAwaitingNextVersion && hasVersions) {
    return renderNewVersionScreen();
  }

  // Show first-email onboarding only when the project has no versions yet.
  const showOnboarding = !hasVersions;

  return showOnboarding ? renderWithoutSidebar() : renderWithSidebar();
};

export default ProjectView;
